import { useWelcomeFoodAccess } from "@/hooks/useWelcomeFoodAccess";
import {
  createFileRoute,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import styles from "@/styles/Food.module.scss";
import { useEffect, useMemo, useState } from "react";
import { CompanyFoodDeal, FoodCompany, FoodDeal } from "@/types";
import {
  allFoodCompaniesOptions,
  snackSupplierOptions,
  useBuilding,
} from "@/api";
import {
  Button,
  Chip,
  Icon,
  InputField,
  LoadingSpinner,
  PopupCard,
  SelectRow,
} from "@app-components";
import { useSessionState } from "@/hooks/useSessionState";
import { createColumnHelper } from "@tanstack/react-table";
import SortableTableHeader from "@/components/table/SortableTableHeader";
import Table from "@/components/table/Table";
import { Tooltip } from "react-tooltip";
import {
  arrayLengthSortingFn,
  firebaseTimestampToDate,
  NOK,
  NUMERIC_DATE_TIME_LOCALE,
  timestampSortingFn,
} from "@/lib";
import FoodDealForm from "@/components/food/FoodDealForm";
import { useFoodDeals } from "@/hooks/useFoodDeals";
import { useRouterStore } from "@/store/zustandStore";
import { useBuildingQuery } from "@/components/RouterSetup";

export const Route = createFileRoute("/_portal/lunch-admin/deals/")({
  component: RouteComponent,
  loader: () => {
    const { queryClient, supplierCompanyId } = useRouterStore.getState();
    if (!queryClient) return;
    queryClient.prefetchQuery(allFoodCompaniesOptions);
    queryClient.prefetchQuery(snackSupplierOptions(supplierCompanyId));
  },
});

const companiesColHelper = createColumnHelper<FoodCompany>();
const searchableCompanyColumns: (keyof FoodCompany)[] = [
  "buildingTenantName",
  "foodDeals",
  "foodMaxIntegratedUsers",
];

const dealsColHelper = createColumnHelper<FoodDeal>();
const searchableDealsColumns: (keyof FoodDeal)[] = ["name", "price"];

function RouteComponent() {
  const { allowFoodAdminAccess } = useWelcomeFoodAccess();
  const navigate = useNavigate();
  const router = useRouter();
  const { t, i18n } = useTranslation();
  const { data: building } = useBuilding();
  const { data: companies, isLoading: isLoadingCompanies } = useBuildingQuery(
    allFoodCompaniesOptions,
  );
  const { deals, isLoading: isLoadingDeals } = useFoodDeals();
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCompanies, setFilteredCompanies] = useState<FoodCompany[]>([]);
  const [filteredDeals, setFilteredDeals] = useState<FoodDeal[]>([]);
  const [dealModalOpen, setDealModalOpen] = useState(false);
  // NOTE: Disabled editing for MVP - will be enabled later
  /* const [editingDeal, setEditingDeal] = useState<FoodDeal | undefined>(
    undefined,
  ); */
  const [view, setView] = useSessionState<"companies" | "deals">(
    "deal-view",
    "companies",
  );

  const dealNameMap = useMemo<Map<string, string>>(() => {
    const map = new Map<string, string>();
    if (deals) {
      deals.forEach((deal) => {
        map.set(deal.dealId, deal.name);
      });
    }
    return map;
  }, [deals]);

  const dealInCompaniesMap = useMemo<Map<string, number>>(() => {
    if (deals) {
      const map = new Map<string, number>();
      deals.forEach((deal) => {
        const count = companies
          ? companies.filter((c) =>
              c.foodDeals?.some((d) => d.dealId === deal.dealId),
            ).length
          : 0;
        map.set(deal.dealId, count);
      });
      return map;
    }
    return new Map();
  }, [companies, deals]);

  useEffect(() => {
    if (searchTerm && !!companies?.length && !!deals?.length) {
      const searchTerms = searchTerm.toLowerCase().split(" ");
      const searchItems = view === "companies" ? companies : deals;
      const searchableColumns =
        view === "companies"
          ? searchableCompanyColumns
          : searchableDealsColumns;
      const filtered = searchItems.filter((item) =>
        searchTerms.every((term) =>
          searchableColumns.some((column) => {
            const val = item[column as keyof typeof item] as
              | string
              | CompanyFoodDeal[];
            return Array.isArray(val)
              ? val.some((aV) => aV.dealId.toLowerCase().includes(term))
              : val?.toString().toLowerCase().includes(term);
          }),
        ),
      );
      if (view === "companies") {
        setFilteredCompanies(filtered as FoodCompany[]);
      } else {
        setFilteredDeals(filtered as FoodDeal[]);
      }
    } else {
      setFilteredCompanies(companies ?? []);
      setFilteredDeals(deals ?? []);
    }
  }, [searchTerm, companies, deals, view]);

  const companiesColumns = useMemo(
    () => [
      companiesColHelper.accessor("buildingTenantName", {
        cell: (info) => info.getValue(),
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("SNACK_ADMIN.TABLE.COMPANY")}
          />
        ),
        meta: {
          label: t("SNACK_ADMIN.TABLE.COMPANY"),
        },
      }),
      companiesColHelper.accessor("totalUsersCount", {
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("COMPANIES_TABLE.TOTAL_USERS")}
          />
        ),
        sortUndefined: -1,
        meta: {
          label: t("COMPANIES_TABLE.TOTAL_USERS"),
        },
      }),
      companiesColHelper.accessor("integratedUsersCount", {
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("COMPANY_SETTINGS.FOOD_SETTINGS_ACTIVE_USERS")}
          />
        ),
        sortUndefined: -1,
        meta: {
          label: t("COMPANY_SETTINGS.FOOD_SETTINGS_ACTIVE_USERS"),
        },
      }),
      companiesColHelper.accessor("disabledUsersCount", {
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("COMPANY_SETTINGS.FOOD_SETTINGS_DISABLED_USERS")}
          />
        ),
        sortUndefined: -1,
        meta: {
          label: t("COMPANY_SETTINGS.FOOD_SETTINGS_DISABLED_USERS"),
        },
      }),
      companiesColHelper.accessor("nonIntegratedUsersCount", {
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("COMPANY_SETTINGS.FOOD_SETTINGS_NOT_INTEGRATED_USERS")}
          />
        ),
        sortUndefined: -1,
        meta: {
          label: t("COMPANY_SETTINGS.FOOD_SETTINGS_NOT_INTEGRATED_USERS"),
        },
      }),
      companiesColHelper.accessor("lunchCreditAmount", {
        cell: (info) => {
          const credit = info.getValue();
          return credit ? NOK.format(credit) : "-";
        },
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("FOOD_ADMIN.DEALS.LUNCH_CREDIT.COLUMN_HEADER")}
          />
        ),
        meta: {
          label: t("FOOD_ADMIN.DEALS.LUNCH_CREDIT.COLUMN_HEADER"),
        },
      }),
      companiesColHelper.accessor("foodDeals", {
        cell: (info) => {
          const companyDeals = info.getValue() || [];
          if (companyDeals.length === 0) {
            return (
              <>
                <Icon
                  name="no_burger"
                  className="dangerIcon"
                  style={{ verticalAlign: "middle" }}
                />
                <Tooltip
                  anchorSelect=".dangerIcon"
                  content={t("FOOD_ADMIN.DEALS.NOT_INTEGRATED")}
                />
              </>
            );
          }
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--wlcm-spacing-xs)",
              }}
            >
              {companyDeals.map(({ dealId, default: isDefault }) => {
                const dealName = dealNameMap.get(dealId);
                return dealName ? (
                  <Chip
                    key={dealId}
                    label={dealName}
                    icon={isDefault ? "star" : undefined}
                    className="tableTag"
                  />
                ) : (
                  <></>
                );
              })}
            </div>
          );
        },
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("FOOD_ADMIN.DEALS.DEALS")}
          />
        ),
        sortingFn: arrayLengthSortingFn,
        meta: {
          label: t("FOOD_ADMIN.DEALS.DEALS"),
        },
      }),
    ],
    [dealNameMap],
  );

  const dealsColumns = useMemo(
    () => [
      dealsColHelper.accessor("dealId", {
        id: "ID",
        header: "ID",
        meta: {
          label: "ID",
        },
      }),
      dealsColHelper.accessor("name", {
        cell: (info) => info.getValue(),
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("FOOD_ADMIN.DEALS.DEAL_NAME")}
          />
        ),
        meta: {
          label: t("FOOD_ADMIN.DEALS.DEAL_NAME"),
        },
      }),
      dealsColHelper.accessor("price", {
        cell: (info) => NOK.format(info.getValue()),
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("FOOD_ADMIN.DEALS.DEAL_PRICE")}
          />
        ),
        meta: {
          label: t("FOOD_ADMIN.DEALS.DEAL_PRICE"),
        },
      }),
      dealsColHelper.accessor("subsidyPercentage", {
        cell: (info) => {
          const percentage = info.getValue();
          if (!percentage) return "-";
          return `${percentage}%`;
        },
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("FOOD_ADMIN.DEALS.SUBSIDY")}
          />
        ),
        meta: {
          label: t("FOOD_ADMIN.DEALS.SUBSIDY"),
        },
      }),
      dealsColHelper.accessor("isGuest", {
        cell: (info) =>
          info.getValue() ? <Chip label={t("FOOD_ADMIN.DEALS.GUEST")} /> : null,
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={<span className="sr-only">Type</span>}
          />
        ),
        meta: {
          label: "Type",
        },
      }),
      dealsColHelper.accessor("createdAt", {
        cell: (info) =>
          NUMERIC_DATE_TIME_LOCALE(i18n.language).format(
            firebaseTimestampToDate(info.getValue()),
          ),
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("USERS.TABLE_HEADERS.CREATED_AT")}
          />
        ),
        sortingFn: timestampSortingFn,
        meta: {
          label: t("USERS.TABLE_HEADERS.CREATED_AT"),
        },
      }),
      // No update functionality for MVP - will be added later
      /* dealsColHelper.accessor("updatedAt", {
        cell: (info) => {
          const val = info.getValue();
          if (!val) return "-";
          return NUMERIC_DATE_TIME_LOCALE(i18n.language).format(
            firebaseTimestampToDate(val),
          );
        },
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("ACCESS_CARD.LAST_UPDATED")}
          />
        ),
        sortingFn: timestampSortingFn,
        meta: {
          label: t("ACCESS_CARD.LAST_UPDATED"),
        },
      }), */
      dealsColHelper.accessor("dealId", {
        cell: (info) => {
          const dealId = info.getValue();
          if (isLoadingCompanies) return <LoadingSpinner size={12} />;
          const count = dealInCompaniesMap.get(dealId) || 0;
          return (
            <>
              {count}
              {count === 0 && (
                <Icon
                  name="warning"
                  className="orangeIcon"
                  style={{
                    marginLeft: "var(--wlcm-spacing-sm)",
                    verticalAlign: "middle",
                  }}
                />
              )}
            </>
          );
        },
        header: (info) => (
          <SortableTableHeader
            info={info}
            label={t("FOOD_ADMIN.DEALS.COMPANIES_USING")}
          />
        ),
        sortingFn: (rowA, rowB) => {
          const getCount = (dealId: string) =>
            dealInCompaniesMap.get(dealId) || 0;
          const countA = getCount(rowA.original.dealId);
          const countB = getCount(rowB.original.dealId);
          return countA - countB;
        },
        meta: {
          label: t("FOOD_ADMIN.DEALS.COMPANIES_USING"),
        },
      }),
    ],
    [dealInCompaniesMap, isLoadingCompanies],
  );

  if (!allowFoodAdminAccess) return null;
  return (
    <>
      <div className="pageHeader">
        <h1>{t("SIDEBAR.FOOD_DEALS")}</h1>
        <p>
          {t("FOOD_ADMIN.DEALS.SUBHEADER", {
            buildingName: building.displayName,
          })}
        </p>
      </div>
      <div className={styles.dealsFilters}>
        <div>
          <InputField
            compact
            icon="search"
            label={t("FOOD_ADMIN.DEALS.SEARCH")}
            hideLabel
            placeholder={t("FOOD_ADMIN.DEALS.SEARCH")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <SelectRow
            tabs
            name="deal-view-selector"
            label="Tabs"
            hideLabel
            options={[
              { label: t("PAGES.COMPANIES.HEADER"), value: "companies" },
              { label: t("FOOD_ADMIN.DEALS.DEALS"), value: "deals" },
            ]}
            value={view}
            onChange={(value) => setView(value as "companies" | "deals")}
          />
        </div>
        {view === "deals" && (
          <Button
            icon="add"
            onClick={() => {
              /* setEditingDeal(undefined); */
              setDealModalOpen(true);
            }}
          >
            {t("FOOD_ADMIN.DEALS.NEW")}
          </Button>
        )}
      </div>
      {view === "companies" ? (
        <Table
          key="food-companies"
          name="food-companies"
          columns={companiesColumns}
          data={filteredCompanies}
          isLoading={isLoadingCompanies}
          onRowClick={({ buildingTenantId }) =>
            navigate({
              to: "/lunch-admin/deals/$companyId",
              params: { companyId: buildingTenantId },
            })
          }
          onRowHover={(row) => {
            router.preloadRoute({
              to: "/lunch-admin/deals/$companyId",
              params: { companyId: row.buildingTenantId },
            });
          }}
          defaultSorting={[{ id: "buildingTenantName", desc: false }]}
          defaultColumnVisibility={{
            totalUsersCount: false,
            foodMaxIntegratedUsers: false,
            disabledUsersCount: false,
            nonIntegratedUsersCount: false,
          }}
          enableColumnControls
          withPagination
        />
      ) : (
        <Table
          key="food-deals"
          name="food-deals"
          columns={dealsColumns}
          data={filteredDeals}
          isLoading={isLoadingDeals}
          /* onRowClick={(deal) => {
            setEditingDeal(deal);
            setDealModalOpen(true);
          }} */
          defaultSorting={[{ id: "createdAt", desc: true }]}
          defaultColumnVisibility={{
            ID: false,
          }}
          noDataMessage={t("FOOD_ADMIN.DEALS.NO_DEALS")}
          enableColumnControls
          withPagination
        />
      )}
      <PopupCard
        open={dealModalOpen}
        onClose={() => {
          // setEditingDeal(undefined);
          setDealModalOpen(false);
        }}
      >
        <FoodDealForm
          // initialValues={editingDeal}
          handleCloseModal={() => {
            // setEditingDeal(undefined);
            setDealModalOpen(false);
          }}
        />
      </PopupCard>
    </>
  );
}
